import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CaptchaConfigUpdateRequest } from '@pryma/auth-shared';
import { api } from '../services/api';

interface ApiCaptchaSettingsResponse {
  success: boolean;
  data: {
    provider: 'turnstile' | 'recaptcha_v3' | 'hcaptcha' | null;
    site_key: string | null;
    enabled: boolean;
    enabled_endpoints: string[];
    score_threshold: number | null;
    has_secret_key: boolean;
  };
}

export interface CaptchaSettings {
  provider: 'turnstile' | 'recaptcha_v3' | 'hcaptcha' | null;
  siteKey: string | null;
  enabled: boolean;
  enabledEndpoints: string[];
  scoreThreshold: number | null;
  hasSecretKey: boolean;
}

function adapt(raw: ApiCaptchaSettingsResponse['data']): CaptchaSettings {
  return {
    provider: raw.provider,
    siteKey: raw.site_key,
    enabled: raw.enabled,
    enabledEndpoints: raw.enabled_endpoints ?? [],
    scoreThreshold: raw.score_threshold,
    hasSecretKey: raw.has_secret_key,
  };
}

function toApiBody(req: CaptchaConfigUpdateRequest) {
  return {
    provider: req.provider,
    site_key: req.siteKey,
    secret_key: req.secretKey,
    score_threshold: req.scoreThreshold,
    enabled_endpoints: req.enabledEndpoints.reduce<Record<string, boolean>>((acc, endpoint) => {
      acc[endpoint] = true;
      return acc;
    }, {}),
    enabled: req.enabled,
  };
}

export const captchaSettingsKeys = {
  platform: ['captcha-settings', 'platform'] as const,
  project: (projectId: string) => ['captcha-settings', 'project', projectId] as const,
};

export function usePlatformCaptchaSettings() {
  return useQuery({
    queryKey: captchaSettingsKeys.platform,
    queryFn: async () => {
      const res = await api.get<ApiCaptchaSettingsResponse>('/api/admin/settings/captcha');
      return adapt(res.data);
    },
  });
}

export function useUpdatePlatformCaptchaSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CaptchaConfigUpdateRequest) => {
      const res = await api.put<ApiCaptchaSettingsResponse>(
        '/api/admin/settings/captcha',
        toApiBody(req)
      );
      return adapt(res.data);
    },
    onSuccess: data => {
      qc.setQueryData(captchaSettingsKeys.platform, data);
    },
  });
}

export function useProjectCaptchaSettings(projectId: string) {
  return useQuery({
    queryKey: captchaSettingsKeys.project(projectId),
    queryFn: async () => {
      const res = await api.get<ApiCaptchaSettingsResponse>(
        `/api/projects/${projectId}/settings/captcha`
      );
      return adapt(res.data);
    },
    enabled: Boolean(projectId),
  });
}

export function useUpdateProjectCaptchaSettings(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: CaptchaConfigUpdateRequest) => {
      const res = await api.put<ApiCaptchaSettingsResponse>(
        `/api/projects/${projectId}/settings/captcha`,
        toApiBody(req)
      );
      return adapt(res.data);
    },
    onSuccess: data => {
      qc.setQueryData(captchaSettingsKeys.project(projectId), data);
    },
  });
}
